import React from 'react';

interface Badge {
  badge: string;
  earned: boolean;
}

interface BadgeBreakdownProps {
  badges: Badge[];
}

/**
 * BadgeBreakdown Component - Shows earned and unearned badges with descriptions
 */
export const BadgeBreakdown: React.FC<BadgeBreakdownProps> = ({ badges }) => {
  const badgeInfo: Record<string, { label: string; description: string; icon: string; color: string }> = {
    new_candidate: {
      label: "New Profile",
      description: "Created your profile",
      icon: "fa-user-plus",
      color: "#085ec8"
    },
    complete_profile: {
      label: "Complete Profile",
      description: "70%+ profile completion",
      icon: "fa-user-check",
      color: "#71ad08"
    },
    active_user: {
      label: "Active",
      description: "Logged in within 180 days",
      icon: "fa-chart-line",
      color: "#085ec8"
    },
    temp_veteran: {
      label: "Temp Veteran",
      description: "3+ accepted temp assignments",
      icon: "fa-medal",
      color: "#fe6718"
    },
    new_temp: {
      label: "New Temp",
      description: "First temp assignment",
      icon: "fa-star",
      color: "#085ec8"
    },
    high_rating: {
      label: "Top Rated",
      description: "4+ rating from practice",
      icon: "fa-trophy",
      color: "#ffc107"
    },
    popular_temp: {
      label: "Popular",
      description: "Rehired by the same practice",
      icon: "fa-fire",
      color: "#fe6718"
    },
    top_rated_temp: {
      label: "Top Rated Temp",
      description: "5.0 rating with 10+ reviews",
      icon: "fa-crown",
      color: "#ffc107"
    }
  };

  const earnedBadges = badges.filter(b => b.earned);
  const unearnedBadges = badges.filter(b => !b.earned);

  return (
    <div className="content-card">
      <div className="content-card__header">
        <h3><i className="fas fa-award me-2"></i>Your Badges</h3>
      </div>
      <div className="content-card__body">
        {earnedBadges.length === 0 ? (
          <div className="empty-state" style={{ padding: '2rem 1rem' }}>
            <div className="empty-state__icon">
              <i className="fas fa-award"></i>
            </div>
            <h4 className="empty-state__title" style={{ fontSize: '1rem' }}>
              No Badges Yet
            </h4>
            <p className="empty-state__description" style={{ fontSize: '0.85rem' }}>
              Complete your profile and stay active to earn badges!
            </p>
          </div>
        ) : (
          <>
            {/* Earned Badges */}
            <div>
              <div className="d-flex flex-column gap-2">
                {earnedBadges.map((badge) => {
                  const info = badgeInfo[badge.badge];
                  if (!info) return null;

                  return (
                    <div
                      key={badge.badge}
                      className="d-flex align-items-center gap-3 p-2 rounded"
                      style={{ background: 'rgba(113, 173, 8, 0.05)', border: '1px solid rgba(113, 173, 8, 0.2)' }}
                    >
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${info.color}, ${info.color}dd)`,
                          color: 'white',
                          flexShrink: 0
                        }}
                      >
                        <i className={`fas ${info.icon}`} style={{ fontSize: '1.2rem' }}></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#2d3748' }}>
                          {info.label}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                          {info.description}
                        </div>
                      </div>
                      <i className="fas fa-check-circle" style={{ color: '#71ad08', fontSize: '1.2rem' }}></i>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BadgeBreakdown;
